import { PersistentSave } from './saveStore.ts';

function toHex(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function fromHex(hex: string): Uint8Array<ArrayBuffer> {
  if (hex.length % 2 !== 0 || !/^[0-9a-fA-F]+$/.test(hex)) {
    throw new Error('Invalid hex string');
  }
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

export class CryptoStore {
  /**
   * Sinh UID ngẫu nhiên: 'usr_' + 8 ký tự hex (32-bit entropy)
   */
  public static generateUID(): string {
    const bytes = new Uint8Array(4);
    globalThis.crypto.getRandomValues(bytes);
    return `usr_${toHex(bytes)}`;
  }

  /**
   * Sinh Secret Key ngẫu nhiên: 'sec_' + 16 ký tự hex (64-bit entropy)
   */
  public static generateSecretKey(): string {
    const bytes = new Uint8Array(8);
    globalThis.crypto.getRandomValues(bytes);
    return `sec_${toHex(bytes)}`;
  }

  /**
   * Dẫn xuất khóa mã hóa AES-256 từ secretKey và salt bằng PBKDF2 (10.000 vòng, SHA-256)
   */
  public static async deriveKey(secretKey: string, salt: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
    const subtle = globalThis.crypto.subtle;
    if (!subtle) {
      throw new Error('Web Crypto API SubtleCrypto is not available in this environment');
    }

    const keyMaterial = await subtle.importKey(
      'raw',
      new TextEncoder().encode(secretKey),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return await subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: 10000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Mã hóa bản lưu PersistentSave bằng AES-256-GCM với Auth Tag 128-bit chống cheat
   * Định dạng xuất: BCVH1.<saltHex>.<ivHex>.<ciphertextAndTagHex>
   */
  public static async encryptSave(save: PersistentSave, secretKey: string): Promise<string> {
    const subtle = globalThis.crypto.subtle;
    if (!subtle) {
      throw new Error('Web Crypto API SubtleCrypto is not available');
    }

    // 1. Tạo Salt ngẫu nhiên 16 bytes (128-bit)
    const salt = new Uint8Array(16);
    globalThis.crypto.getRandomValues(salt);

    // 2. Dẫn xuất CryptoKey
    const key = await this.deriveKey(secretKey, salt);

    // 3. Tạo IV ngẫu nhiên 12 bytes (96-bit) chuẩn cho AES-GCM
    const iv = new Uint8Array(12);
    globalThis.crypto.getRandomValues(iv);

    // 4. Bổ sung metadata chống replay/revert
    const payload: PersistentSave & { saveSeq: number; exportedAt: number } = {
      ...save,
      saveSeq: (save.saveSeq ?? 0) + 1,
      exportedAt: Date.now(),
    };

    const plaintextBytes = new TextEncoder().encode(JSON.stringify(payload));

    // 5. Mã hóa bằng AES-GCM (Ciphertext tự động gắn Auth Tag 16 bytes ở cuối)
    const ciphertextBuffer = await subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      key,
      plaintextBytes
    );

    const saltHex = toHex(salt);
    const ivHex = toHex(iv);
    const cipherHex = toHex(ciphertextBuffer);

    return `BCVH1.${saltHex}.${ivHex}.${cipherHex}`;
  }

  /**
   * Giải mã token sao lưu. Bắt buộc ném lỗi nếu token bị can thiệp dù chỉ 1 bit hoặc sai SecretKey
   */
  public static async decryptSave(token: string, secretKey: string): Promise<PersistentSave> {
    const subtle = globalThis.crypto.subtle;
    if (!subtle) {
      throw new Error('Web Crypto API SubtleCrypto is not available');
    }

    const parts = token.trim().split('.');
    if (parts.length !== 4 || parts[0] !== 'BCVH1') {
      throw new Error('Mã sao lưu không hợp lệ, đã bị chỉnh sửa trái phép hoặc khóa bí mật không đúng!');
    }

    const [, saltHex, ivHex, cipherHex] = parts;
    if (!saltHex || !ivHex || !cipherHex) {
      throw new Error('Mã sao lưu không hợp lệ, đã bị chỉnh sửa trái phép hoặc khóa bí mật không đúng!');
    }

    let salt: Uint8Array<ArrayBuffer>;
    let iv: Uint8Array<ArrayBuffer>;
    let cipherBytes: Uint8Array<ArrayBuffer>;

    try {
      salt = fromHex(saltHex);
      iv = fromHex(ivHex);
      cipherBytes = fromHex(cipherHex);
    } catch {
      throw new Error('Mã sao lưu không hợp lệ, đã bị chỉnh sửa trái phép hoặc khóa bí mật không đúng!');
    }

    try {
      const key = await this.deriveKey(secretKey, salt);
      const decryptedBuffer = await subtle.decrypt(
        {
          name: 'AES-GCM',
          iv,
        },
        key,
        cipherBytes
      );

      const jsonStr = new TextDecoder().decode(decryptedBuffer);
      const parsed = JSON.parse(jsonStr) as PersistentSave;
      if (!parsed || typeof parsed !== 'object' || typeof parsed.version !== 'number') {
        throw new Error('Dữ liệu sao lưu không đúng định dạng!');
      }
      return parsed;
    } catch (e: unknown) {
      if (e instanceof Error && e.message === 'Dữ liệu sao lưu không đúng định dạng!') {
        throw e;
      }
      // AES-GCM Authentication tag verification failed (MAC check failed) or wrong secret key
      throw new Error('Mã sao lưu không hợp lệ, đã bị chỉnh sửa trái phép hoặc khóa bí mật không đúng!');
    }
  }
}
